import { ApiError, getProfile, getSelectedTenant, getToken } from './client';

/**
 * Cầu nối tới service identity.
 *
 * Tách khỏi `client.ts` thay vì thêm một cờ vào `apiFetch`: hai service là hai
 * chủ sở hữu dữ liệu khác nhau, và một hàm nhận cờ "gọi chỗ khác" làm mờ ranh
 * giới đó. Token thì dùng chung — identity và delivery-api chấp nhận cùng một
 * access token (cùng bí mật, cùng `aud`).
 */
function identityUrl(): string {
  const value = import.meta.env['VITE_IDENTITY_API_URL'];
  return (typeof value === 'string' && value ? value : 'http://localhost:3005').replace(/\/+$/, '');
}

export interface IdentityRequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export async function identityFetch<T>(
  path: string,
  options: IdentityRequestOptions = {},
): Promise<T> {
  const url = new URL(`${identityUrl()}${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  const selected = getSelectedTenant();
  if (selected && !url.searchParams.has('tenantId')) {
    const profile = getProfile();
    if (profile?.roles?.includes('system_admin')) {
      url.searchParams.set('tenantId', selected);
    }
  }

  const hasBody = options.body !== undefined;
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken()}` };
  if (hasBody) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiError(0, 'api.error.coreUnavailable');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string };
    throw new ApiError(response.status, body.code ?? 'api.error.internal');
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
